import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Zap, Lock, AlertCircle } from "lucide-react";

function formatApiError(detail) {
  if (detail == null) return "Login failed. Please check your credentials.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

function useLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [email, password, login, navigate]);

  return { email, setEmail, password, setPassword, error, loading, handleSubmit };
}

const LoginForm = ({ email, setEmail, password, setPassword, error, loading, handleSubmit }) => (
  <form onSubmit={handleSubmit} className="border border-white/10 p-8 space-y-6" data-testid="admin-login-form">
    {error && (
      <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/30 p-4 text-red-400 text-sm" data-testid="login-error-message">
        <AlertCircle className="w-4 h-4 shrink-0" />
        {error}
      </div>
    )}

    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Email</label>
      <Input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="admin@emfaware.com"
        className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
        required
        data-testid="admin-email-input"
      />
    </div>

    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-bold text-white/50 mb-2 block">Password</label>
      <Input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Enter password"
        className="rounded-none bg-zinc-900/50 border border-white/15 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] px-4 py-3 h-12 text-white placeholder:text-white/40"
        required
        data-testid="admin-password-input"
      />
    </div>

    <Button
      type="submit"
      disabled={loading}
      className="w-full rounded-none uppercase tracking-widest font-bold bg-[#FF3B30] text-white border-2 border-[#FF3B30] hover:bg-transparent hover:text-[#FF3B30] transition-all duration-300 h-12"
      data-testid="admin-login-submit-btn"
    >
      <Lock className="w-4 h-4 mr-2" />
      {loading ? "AUTHENTICATING..." : "ACCESS DASHBOARD"}
    </Button>
  </form>
);

export default function AdminLogin() {
  const formProps = useLoginForm();

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <a href="/" className="inline-flex items-center gap-2 mb-6">
            <Zap className="w-6 h-6 text-[#FF3B30]" />
            <span className="font-['Anton'] text-xl uppercase tracking-wider text-white">
              EMF<span className="text-[#FF3B30]">AWARE</span>
            </span>
          </a>
          <h1 className="font-['Anton'] text-3xl sm:text-4xl uppercase tracking-tight text-white" data-testid="admin-login-title">
            ADMIN <span className="text-[#FF3B30]">LOGIN</span>
          </h1>
        </div>

        <LoginForm {...formProps} />

        <p className="text-center text-white/30 text-xs mt-6">
          <a href="/" className="hover:text-[#FF3B30] transition-colors">Back to website</a>
        </p>
      </div>
    </div>
  );
}
